import * as React from 'react';
import { api } from '../api';
import { AGE_GROUPS } from '../data';
import { useApi, useAsyncAction } from '../hooks';
import { Icon } from '../icons.jsx';
import { Button, Field, Input, Modal, Placeholder, ProviderPhoto, Select } from '../ui.jsx';

const formatPhone = (value) => {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const WaitlistModal = ({ open, onClose, providerId, initialAge }) => {
  const {
    data: provider,
    loading,
    error,
  } = useApi(
    () => (providerId ? api.providers.get(providerId) : Promise.resolve(null)),
    [providerId],
  );
  const [age, setAge] = React.useState(initialAge || 'Infant');
  const [name, setName] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [submitted, setSubmitted] = React.useState(null);
  const joinAction = useAsyncAction((entry) => api.waitlists.create(entry));

  React.useEffect(() => {
    if (!open) return;
    setAge(
      initialAge && provider?.ages.includes(initialAge)
        ? initialAge
        : provider?.ages[0] || 'Infant',
    );
    setName('');
    setPhone('');
    setSubmitted(null);
  }, [open, providerId, initialAge, provider]);

  const phoneIsValid = phone.replace(/\D/g, '').length === 10;
  const canSubmit = Boolean(providerId && name.trim() && phoneIsValid && !joinAction.loading);

  const submit = async () => {
    if (!canSubmit) return;
    try {
      const entry = await joinAction.run({
        providerId,
        parentName: name.trim(),
        childAge: age,
        phone,
      });
      setSubmitted(entry);
    } catch {
      // useAsyncAction exposes the error in the modal.
    }
  };

  const p = provider || {
    name: loading ? 'Loading…' : 'Child care provider',
    image: '',
    tone: 'A',
    ages: AGE_GROUPS,
  };

  return (
    <Modal open={open} onClose={onClose} width={520} ariaLabel={`Join the waitlist at ${p.name}`}>
      <div className="waitlist-modal-header">
        <div className="waitlist-modal-provider">
          {p.image
            ? <ProviderPhoto src={p.image} alt="" radius={10} style={{ width: 42, height: 42 }}/>
            : <Placeholder tone={p.tone} radius={10} style={{ width: 42, height: 42 }} label=""/>}
          <div>
            <span>Join the waitlist at</span>
            <strong>{p.name}</strong>
          </div>
        </div>
        <button type="button" aria-label="Close" onClick={onClose}>
          <Icon name="x" size={16}/>
        </button>
      </div>

      <div className="waitlist-modal-body">
        {error && (
          <div className="waitlist-modal-error" role="alert">
            We couldn’t load this provider: {error.message}
          </div>
        )}
        {joinAction.error && (
          <div className="waitlist-modal-error" role="alert">
            We couldn’t add you to the waitlist: {joinAction.error.message}
          </div>
        )}

        {submitted ? (
          <div className="waitlist-success">
            <span><Icon name="check" size={26}/></span>
            <h2>You’re on the waitlist</h2>
            <p>
              {p.name} will contact you at {submitted.phone} when a {submitted.childAge} spot may
              be available.
            </p>
            <div>
              <strong>{submitted.childAge}</strong>
              <span>Joined today · No account needed</span>
            </div>
          </div>
        ) : (
          <>
            <div className="waitlist-modal-intro">
              <h2>Tell the center how to reach you</h2>
              <p>
                Joining is free. The provider manages this waitlist and will contact you directly.
              </p>
            </div>

            <div className="waitlist-form">
              <Field id="waitlist-age" label="Age group">
                <Select
                  icon="user"
                  value={age}
                  onChange={setAge}
                  options={(provider?.ages || AGE_GROUPS).map((group) => ({
                    value: group,
                    label: group,
                  }))}
                />
              </Field>
              <Field id="waitlist-name" label="Your name">
                <Input
                  icon="user"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Jordan Lee"
                  autoComplete="name"
                />
              </Field>
              <Field
                id="waitlist-phone"
                label="Phone number"
                hint="The provider will use this number for waitlist updates."
              >
                <Input
                  icon="phone"
                  value={phone}
                  onChange={(event) => setPhone(formatPhone(event.target.value))}
                  placeholder="(215) 555-0123"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </Field>
            </div>

            <div className="waitlist-privacy">
              <Icon name="shield" size={16}/>
              <span>Your information is shared only with this provider for waitlist follow-up.</span>
            </div>
          </>
        )}
      </div>

      <div className="waitlist-modal-footer">
        {submitted ? (
          <Button variant="primary" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              variant="primary"
              icon="check"
              disabled={!canSubmit}
              onClick={submit}
            >
              {joinAction.loading ? 'Joining…' : 'Join waitlist'}
            </Button>
          </>
        )}
      </div>
    </Modal>
  );
};
